<template>
  <div class="overview">
    <!-- KPI Cards Grid -->
    <div class="kpi-grid">
      <dashboard-v2-kpi-card
        type="ca"
        :kpi="kpiData.ca"
        :view="view"
        :date-from="dateFrom"
        :date-to="dateTo"
        :current-month-label="currentMonthLabel"
        :current-year="currentYear"
        :expanded="compareExpanded.ca"
        @toggle-compare="toggleCompare"
      />
      <dashboard-v2-kpi-card
        type="sessions"
        :kpi="kpiData.sessions"
        :view="view"
        :date-from="dateFrom"
        :date-to="dateTo"
        :current-month-label="currentMonthLabel"
        :current-year="currentYear"
        :expanded="compareExpanded.sessions"
        @toggle-compare="toggleCompare"
      />
      <dashboard-v2-kpi-card
        type="rpm"
        :kpi="kpiData.rpm"
        :view="view"
        :date-from="dateFrom"
        :date-to="dateTo"
        :current-month-label="currentMonthLabel"
        :current-year="currentYear"
        :expanded="compareExpanded.rpm"
        @toggle-compare="toggleCompare"
      />
    </div>

    <!-- Quick Stats Summary -->
    <v-card flat class="summary-card">
      <div class="summary-header">
        <h3 class="summary-title">Résumé rapide</h3>
        <v-chip small outlined :color="RMR_INDIGO" class="summary-badge">
          {{ view === 'month' ? 'Ce mois' : 'Cette année' }}
        </v-chip>
      </div>
      <v-row dense>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-mini">
            <div class="stat-mini-icon stat-mini-icon--ca">
              <v-icon size="18">{{ icons.mdiCurrencyEur }}</v-icon>
            </div>
            <div>
              <div class="stat-mini-label">CA Estimation</div>
              <div class="stat-mini-value">{{ caProjection }}</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-mini">
            <div class="stat-mini-icon stat-mini-icon--sessions">
              <v-icon size="18">{{ icons.mdiPercent }}</v-icon>
            </div>
            <div>
              <div class="stat-mini-label">Ratio CA/Obj</div>
              <div class="stat-mini-value">{{ kpiData.ca.ratio }}%</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-mini">
            <div class="stat-mini-icon stat-mini-icon--rpm">
              <v-icon size="18">{{ icons.mdiSpeedometer }}</v-icon>
            </div>
            <div>
              <div class="stat-mini-label">RPM Moyen</div>
              <div class="stat-mini-value">{{ rpmCurrent }}</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-mini">
            <div class="stat-mini-icon stat-mini-icon--green">
              <v-icon size="18">{{ icons.mdiTrendingUp }}</v-icon>
            </div>
            <div>
              <div class="stat-mini-label">Ratio CA/Estimations</div>
              <div class="stat-mini-value">{{ caEstimationRatio }}</div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import { mdiCurrencyEur, mdiPercent, mdiSpeedometer, mdiTrendingUp } from '@mdi/js'
import { computed, reactive, watch } from '@vue/composition-api'
import DashboardV2KpiCard from '@/components/general/dashboard-v2/cards/DashboardV2KpiCard.vue'
import {
  DEFAULT_COMPARISON,
  DEFAULT_TRAFFIC_SOURCE,
  buildCacheOptions,
  buildKpiDateParams,
  buildScopeParams,
} from '@/store/dashboard-v2'
import { monthsFrArray, formatDateRangeToken, formatMonthToken } from '@/utils/dates'
import useProxy from '@/hooks/useProxy'
import useAxios from '@/hooks/useAxios'
import { RMR_INDIGO } from '@/styles/tokens'

const COMPONENT_NAME = 'DashboardV2Overview'

const toObjectiveTrend = (ratio) => (Number(ratio || 0) >= 1 ? 'up' : 'down')

const buildFingerprint = (state, view) => JSON.stringify({
  source: state.trafficSource || DEFAULT_TRAFFIC_SOURCE,
  view,
  dateFrom: state.dateFrom,
  dateTo: state.dateTo,
  sites: [...(state.sites || [])].sort(),
  partnerViews: [...(state.partnerViews || [])].sort(),
  partnerViewGroups: [...(state.partnerViewGroups || [])].sort(),
  siteGroups: [...(state.siteGroups || [])].sort(),
})

const DEFAULT_KPI_DATA = {
  ca:       { value: null, objective: null, projection: null, progress: 0, ratio: 0, estimationRatio: null, comparison: { ...DEFAULT_COMPARISON } },
  sessions: { value: null, objective: null, projection: null, progress: 0, ratio: 0, comparison: { ...DEFAULT_COMPARISON } },
  rpm:      { value: null, objective: null, current: null,    progress: 0, ratio: 0, comparison: { ...DEFAULT_COMPARISON } },
}

const icons = { mdiCurrencyEur, mdiPercent, mdiSpeedometer, mdiTrendingUp }

export default {
  name: COMPONENT_NAME,
  components: { DashboardV2KpiCard },
  props: {
    view: { type: String, default: 'month' },
  },
  setup(props) {
    const { store } = useProxy()
    const { axiosGet } = useAxios()

    // => CONST
    const compareExpanded = reactive({ ca: false, sessions: false, rpm: false })

    // => METHODS
    let latestComparisonRequestId = 0

    const fetchComparisons = async () => {
      latestComparisonRequestId += 1
      const requestId = latestComparisonRequestId
      const requestGeneration = store.getters['dashboardV2/getRefreshGeneration']
      const state = store.state.dashboardV2
      const requestFingerprint = buildFingerprint(state, props.view)
      const currentKpiData = store.getters['dashboardV2/getOverviewKpiData']
      if (!currentKpiData) return

      const params = {
        ...buildKpiDateParams(props.view, state.dateFrom, state.dateTo),
        ...buildScopeParams(state, true),
      }

      try {
        const { data } = await axiosGet('/dashboard/kpi-summary-previous', params, buildCacheOptions(state.dateTo))
        if (requestId !== latestComparisonRequestId) return
        if (requestGeneration !== store.getters['dashboardV2/getRefreshGeneration']) return
        const currentFingerprint = buildFingerprint(store.state.dashboardV2, props.view)
        if (requestFingerprint !== currentFingerprint) return
        const comparisons = data?.comparisons || {}
        const prevPeriod =
          comparisons.previous_month ||
          comparisons.previous_year ||
          comparisons.previous_period ||
          null
        const yearPeriod = comparisons.same_month_previous_year || comparisons.same_period_previous_year || null

        const prevLabel = props.view === 'month'
          ? `vs M-1 (${formatMonthToken(prevPeriod?.date_start, false)})`
          : props.view === 'year'
            ? `vs Annee N-1 (${(prevPeriod?.date_start || '').substring(0, 4)})`
            : 'vs Periode prec.'
        const yearLabel = props.view === 'month'
          ? `vs N-1 (${formatMonthToken(yearPeriod?.date_start, true)})`
          : props.view === 'range'
            ? `vs N-1 (${formatDateRangeToken(yearPeriod?.date_start, yearPeriod?.date_end)})`
            : 'vs N-1'
        const rangePrevLabel = `vs ${formatDateRangeToken(prevPeriod?.date_start, prevPeriod?.date_end)}`

        const freshKpiData = store.getters['dashboardV2/getOverviewKpiData']
        if (!freshKpiData) return
        const next = JSON.parse(JSON.stringify(freshKpiData))
        const metrics = ['ca', 'sessions', 'rpm']
        metrics.forEach((metric) => {
          const prevReal = Number(prevPeriod?.[metric]?.real || 0)
          const yearReal = Number(yearPeriod?.[metric]?.real || 0)
          const prevObjectiveRatio = Number(prevPeriod?.[metric]?.objective_ratio || 0)
          const yearObjectiveRatio = Number(yearPeriod?.[metric]?.objective_ratio || 0)
          next[metric].comparison = {
            prevLabel: props.view === 'range' ? rangePrevLabel : prevLabel,
            yearLabel: yearPeriod ? yearLabel : '',
            prev: prevReal,
            prevPercent: prevObjectiveRatio,
            prevTrend: toObjectiveTrend(prevObjectiveRatio),
            year: yearPeriod ? yearReal : null,
            yearPercent: yearPeriod ? yearObjectiveRatio : null,
            yearTrend: yearPeriod ? toObjectiveTrend(yearObjectiveRatio) : '',
          }
        })

        store.commit('dashboardV2/setOverviewKpiData', next)
        store.commit('dashboardV2/setOverviewComparisonsLoaded', true)
      } catch (error) {
        console.error('Error fetching KPI comparisons:', error)
        compareExpanded.ca = false
        compareExpanded.sessions = false
        compareExpanded.rpm = false
      }
    }

    const toggleCompare = async () => {
      const shouldOpenAll = !compareExpanded.ca
        || !compareExpanded.sessions
        || !compareExpanded.rpm

      if (shouldOpenAll && !comparisonsLoaded.value) {
        await fetchComparisons()
      }

      compareExpanded.ca = shouldOpenAll
      compareExpanded.sessions = shouldOpenAll
      compareExpanded.rpm = shouldOpenAll
    }

    // => COMPUTED
    const kpiData = computed(() =>
      store.getters['dashboardV2/getOverviewKpiData'] || DEFAULT_KPI_DATA
    )
    const comparisonsLoaded = computed(() =>
      store.getters['dashboardV2/getOverviewComparisonsLoaded']
    )
    const month = computed(() => store.getters['dashboardV2/getMonth'])
    const dateFrom = computed(() => store.getters['dashboardV2/getDateFrom'])
    const dateTo = computed(() => store.getters['dashboardV2/getDateTo'])
    const currentMonthLabel = computed(() => {
      if (!month.value) return ''
      const [, m] = month.value.split('-')
      return monthsFrArray[parseInt(m) - 1] || ''
    })
    const currentYear = computed(() => (month.value ? month.value.split('-')[0] : ''))

    const caProjection = computed(() => kpiData.value?.ca?.projection ?? '--')
    const rpmCurrent = computed(() => kpiData.value?.rpm?.current ?? '--')
    const caEstimationRatio = computed(() => kpiData.value?.ca?.estimationRatio ?? '--')

    // => WATCH
    watch(
      () => store.getters['dashboardV2/getTrafficSource'],
      () => {
        latestComparisonRequestId += 1
        store.commit('dashboardV2/setOverviewComparisonsLoaded', false)
      }
    )

    watch(comparisonsLoaded, (value) => {
      if (value) return
      if (compareExpanded.ca || compareExpanded.sessions || compareExpanded.rpm) {
        fetchComparisons()
      }
    })

    // => DATA
    return {
      icons,
      compareExpanded,
      kpiData,
      dateFrom,
      dateTo,
      currentMonthLabel,
      currentYear,
      caProjection,
      rpmCurrent,
      caEstimationRatio,
      toggleCompare,
      RMR_INDIGO,
    }
  },
}
</script>

<style lang="scss" scoped>
.overview {
  display: flex;
  flex-direction: column;
  gap: $rmr-space-xl;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: $rmr-space-lg;
}

.summary-card {
  border-radius: $rmr-radius-2xl !important;
  padding: $rmr-space-lg $rmr-space-xl;
  box-shadow: $rmr-shadow-card !important;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $rmr-space-md;
  padding-bottom: $rmr-space-sm;
  border-bottom: 1px solid $rmr-border;
}

.summary-title {
  font-size: $rmr-fs-body;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-text-secondary;
  letter-spacing: 0.5px;
  margin: 0;
}

.summary-badge {
  font-weight: 700 !important;
  font-size: $rmr-fs-sm !important;
  letter-spacing: 0.5px;
}

.stat-mini {
  display: flex;
  align-items: center;
  gap: $rmr-space-sm;
  padding: $rmr-space-sm;
  background: $rmr-surface-1;
  border-radius: $rmr-radius-lg;
  border: 1px solid $rmr-border;
  transition: all 0.2s;

  &:hover {
    background: $rmr-surface-2;
    border-color: $rmr-border-hover;
  }
}

.stat-mini-icon {
  width: 36px;
  height: 36px;
  border-radius: $rmr-radius-md;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-mini-icon--ca {
  background: $rmr-ca-soft !important;
  color: $rmr-ca !important;
}

.stat-mini-icon--sessions {
  background: $rmr-sessions-soft !important;
  color: $rmr-sessions !important;
}

.stat-mini-icon--rpm {
  background: $rmr-rpm-soft !important;
  color: $rmr-rpm !important;
}

.stat-mini-icon--green {
  background: $rmr-green-bg !important;
  color: $rmr-green !important;
}

.stat-mini-label {
  font-size: $rmr-fs-sm;
  font-weight: 600;
  color: $rmr-text-subtle;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.stat-mini-value {
  font-size: $rmr-fs-stat;
  font-weight: 800;
  color: $rmr-text-primary;
  margin-top: 2px;
}

@media (max-width: 960px) {
  .kpi-grid { grid-template-columns: 1fr; }
}
</style>
